import { Button } from "@/components/ui/button";
import type { PurposeDefView } from "@/lib/api";
import { jobDescription, jobLabel } from "@/lib/models";
import { cn } from "@/lib/utils";
import { ChevronDown, Lock, SlidersHorizontal } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

// The one layout for choosing models, on the organization's Models page and
// on each Boo's Models tab: Manage models (or who manages this), after a
// short intro on a Boo's tab (the org page says it in its subtitle), notices, Conversations, then the other jobs. Jobs that follow
// Conversations stay folded away until someone asks for them.

export function ModelJobs({
  intro,
  managedBy,
  onManage,
  notices,
  jobs,
  picker,
  status,
  shownByDefault,
  openJob,
  onOpenJobChange,
  othersNote,
  children,
}: {
  // Omitted where the page's subtitle already says it.
  intro?: string;
  // Read-only: who can change these. Shown instead of edit affordances.
  managedBy?: string | null;
  // Opens Manage models; only for people who can manage the org's models.
  onManage?: () => void;
  notices?: ReactNode;
  jobs: PurposeDefView[];
  picker: (job: PurposeDefView, big: boolean, open: PickerOpen) => ReactNode;
  // A line under a picker (what runs right now), if the page has one.
  status?: (job: PurposeDefView) => ReactNode;
  // Jobs set to something other than Conversations, listed without unfolding.
  shownByDefault: (job: PurposeDefView) => boolean;
  // The job whose dropdown is open, when opened from elsewhere ("Change").
  openJob?: string | null;
  onOpenJobChange?: (job: string | null) => void;
  // A line under Other jobs.
  othersNote?: string;
  children?: ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  const chat = jobs.find((j) => j.id === "chat");
  const others = jobs.filter((j) => j.id !== "chat");
  const visible = showAll ? others : others.filter((j) => shownByDefault(j) || j.id === openJob);
  // A job opened from elsewhere scrolls into view first, so its list opens
  // next to it.
  useEffect(() => {
    if (openJob) document.getElementById(`job-${openJob}`)?.scrollIntoView({ block: "center" });
  }, [openJob]);
  const openFor = (job: PurposeDefView): PickerOpen => ({
    open: openJob === job.id,
    onOpenChange: (o) => onOpenJobChange?.(o ? job.id : null),
  });
  const hidden = others.length - others.filter(shownByDefault).length;

  return (
    <div className="flex max-w-5xl flex-col gap-8 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        {intro && <p className="max-w-2xl text-sm text-muted-foreground">{intro}</p>}
        <div className="flex items-center gap-2">
          {managedBy && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
              <Lock className="size-3" />
              {managedBy}
            </span>
          )}
          {onManage && (
            <Button onClick={onManage}>
              <SlidersHorizontal className="size-4" />
              Manage models
            </Button>
          )}
        </div>
      </div>

      {notices}

      {chat && (
        <section className="space-y-3">
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold">{jobLabel(chat)}</h3>
            <p className="text-sm text-muted-foreground">{jobDescription(chat)} Other jobs use this model too, unless you choose another below.</p>
          </div>
          <div id="job-chat" className="space-y-3 rounded-lg border p-4">
            {picker(chat, true, openFor(chat))}
            {status?.(chat)}
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="space-y-3">
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold">Other jobs</h3>
            <p className="text-sm text-muted-foreground">
              Use a faster or cheaper model for background work, or a specialist where it matters.
              {othersNote ? ` ${othersNote}` : ""}
            </p>
          </div>
          {visible.length === 0 ? (
            <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
              Every other job uses the conversation model or its standard choice.
            </p>
          ) : (
            <div className="divide-y rounded-lg border">
              {visible.map((job) => (
                <div key={job.id} id={`job-${job.id}`} className="grid gap-2 p-4 sm:grid-cols-[15rem_1fr] sm:items-start">
                  <div className="pt-1">
                    <p className="text-sm font-medium">{jobLabel(job)}</p>
                    <p className="text-xs text-muted-foreground">{jobDescription(job)}</p>
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    {picker(job, false, openFor(job))}
                    {status?.(job)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {hidden > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="-ml-2 gap-1.5 text-muted-foreground"
              onClick={() => setShowAll((v) => !v)}
            >
              <ChevronDown className={cn("size-4 transition-transform", showAll && "rotate-180")} />
              {showAll ? "Show fewer jobs" : `Show all jobs (${hidden} more)`}
            </Button>
          )}
        </section>
      )}

      {children}
    </div>
  );
}

export type PickerOpen = { open: boolean; onOpenChange: (open: boolean) => void };

// The bar that appears once something changed.
export function SaveBar({
  message,
  detail,
  error,
  saving,
  onDiscard,
  onSave,
}: {
  message?: string;
  detail?: string;
  error?: string | null;
  saving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  return (
    <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background/95 px-4 py-3 shadow-lg backdrop-blur [@media(prefers-reduced-transparency:reduce)]:bg-background [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none">
      <div className="min-w-0 text-sm">
        {error ? <p className="text-destructive">{error}</p> : <p>{message ?? "Unsaved changes"}</p>}
        {detail && !error && <p className="text-xs text-muted-foreground">{detail}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button size="sm" onClick={onSave} disabled={!!error} loading={saving}>
          Save changes
        </Button>
      </div>
    </div>
  );
}
